'use client';

import type { BlockDto } from '@klickibunti/pagebuilder-react';
import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useEffect, useId, useRef, useState } from 'react';
import type { SubjectItemResolved, SubjectsWidgetResolved } from './types';
import { Carousel } from '@/components/Carousel';
import { Icon } from '@/components/Icon';
import { Section } from '@/components/Section';
import { SlotRenderer } from '@/components/SlotRenderer';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { sanitize } from '@/lib/sanitize';
import { scrollToSettled } from '@/lib/scrollToSettled';
import { resolveSubjectNameForTabAnchor } from '@/lib/tabAnchorFallback';

interface SubjectTabProps {
  tab: SubjectItemResolved;
  isActive: boolean;
  tabId: string;
  panelId: string;
  onSelect: () => void;
}

const SubjectTab = ({
  tab,
  isActive,
  tabId,
  panelId,
  onSelect,
}: SubjectTabProps) => (
  <button
    role="tab"
    id={tabId}
    aria-selected={isActive}
    aria-controls={panelId}
    onClick={onSelect}
    className="flex max-w-22 shrink-0 flex-col items-center gap-2 text-center break-words hyphens-auto"
  >
    <span
      className={classNames(
        'flex size-16 items-center justify-center rounded-full transition-colors md:size-12 lg:size-18',
        isActive
          ? 'bg-primary text-white'
          : 'bg-secondary-lighter text-foreground',
      )}
    >
      <Icon src={tab.icon} size={32} className="transition-colors" />
    </span>
    <span
      className={classNames(
        'text-sm',
        isActive ? 'font-medium' : 'font-normal',
      )}
    >
      {tab.name}
    </span>
  </button>
);

// A description-zone block renders content unless it is a snippet-reference
// whose snippet was not found (`.resolved.snippet` is null — attached by the
// backend's resolveNestedLookupWidgets).
function blockHasContent(block: BlockDto): boolean {
  if (block.componentType === 'widgets.snippet-reference') {
    const resolved = (block as { resolved?: { snippet?: unknown } }).resolved;
    return resolved?.snippet != null;
  }
  return true;
}

export const SubjectsWidgetComponent = ({
  resolved,
}: {
  resolved: SubjectsWidgetResolved;
}) => {
  const { substitutePlaceholders: sub } = useRenderContext();
  const [activeIndex, setActiveIndex] = useState(0);
  const baseId = useId();

  // The authored list is authoritative: every subject an editor placed on the
  // widget renders, in its authored order, on every page. The widget does not
  // consult the location's offered subjects — varying the list per Standort is
  // an editorial job (a different widget instance or content snippet).
  const tabs = resolved.subjects;

  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // Deep-link fallback: since tab ids are generated via useId() and aren't
  // guessable/stable, neither a named link (#mathe) nor the legacy site's
  // numbered jumppoint (#tab-<anything>-<0|1|2>) can rely on native anchor
  // scrolling to reach a tab. When enabled, resolve either hash shape on load
  // (and on hashchange) and select + scroll to the matching subject tab.
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_TAB_ANCHOR_SCROLL_FALLBACK_ENABLED !== 'true') {
      return;
    }

    // This runs the moment React hydrates, while blocks above the widget are
    // still growing into their final size, so a single scroll lands against a
    // layout that no longer exists by the time it finishes (SH-559).
    // `scrollToSettled` holds the alignment until the page stops moving.
    let cancelScroll: (() => void) | undefined;

    const applyTabAnchorFallback = () => {
      const subjectName = resolveSubjectNameForTabAnchor(window.location.hash);
      if (!subjectName) {
        return;
      }
      const index = tabsRef.current.findIndex(
        (tab) => tab.name === subjectName,
      );
      if (index !== -1) {
        setActiveIndex(index);
      }
      cancelScroll?.();
      cancelScroll = scrollToSettled(() => document.getElementById('faecher'));
    };

    applyTabAnchorFallback();
    window.addEventListener('hashchange', applyTabAnchorFallback);
    return () => {
      cancelScroll?.();
      window.removeEventListener('hashchange', applyTabAnchorFallback);
    };
  }, []);

  if (tabs.length === 0) {
    return null;
  }

  // Clamped: a tab list that shrank under a stale index must still show a
  // panel rather than none.
  const shownIndex = Math.min(activeIndex, tabs.length - 1);

  const selectTab = (i: number, tabName: string) => () => {
    pushCmsEventTracking(resolved.tracking, { label: tabName });
    setActiveIndex(i);
  };

  // Right-column resolution, per tab: (1) the item's own description; (2) the
  // widget-level description zone (inline text and/or a resolved snippet);
  // (3) the widget-level fallback rich text. Works identically with and
  // without a location in context.
  const descriptionBlocks = resolved.description ?? [];
  const hasDescriptionZone =
    descriptionBlocks.length > 0 && descriptionBlocks.some(blockHasContent);
  const fallback = resolved.fallback;

  const phone = sub(resolved.phone ?? '');
  const phoneDisplay = sub(resolved.matelsoPhoneLabel ?? '') || phone;

  return (
    <Section id="faecher">
      <div className="space-y-8">
        <div
          role="tablist"
          className="scrollbar-hide hidden justify-evenly overflow-x-auto pb-2 md:flex"
        >
          {tabs.map((tab, i) => (
            <SubjectTab
              key={tab.key ?? `item-${i}`}
              tab={tab}
              isActive={i === shownIndex}
              tabId={`${baseId}-tab-${i}`}
              panelId={`${baseId}-panel-${i}`}
              onSelect={selectTab(i, tab.name)}
            />
          ))}
        </div>

        <Carousel
          className="md:hidden"
          slidesPerView={3}
          prevLabel="Vorherige Fächer"
          nextLabel="Nächste Fächer"
          slides={tabs.map((tab, i) => (
            <div key={tab.key ?? `item-${i}`} className="flex justify-center">
              <SubjectTab
                tab={tab}
                isActive={i === shownIndex}
                tabId={`${baseId}-mtab-${i}`}
                panelId={`${baseId}-panel-${i}`}
                onSelect={selectTab(i, tab.name)}
              />
            </div>
          ))}
        />

        {/*
          Every subject gets its panel, and all but the active one carry the
          `hidden` attribute. A crawler reads the server HTML and never clicks
          a tab, so a widget that rendered only the active panel kept each
          subject's heading and topic list out of reach of indexing — the
          legacy site had all of it in the source (SH-564). Tailwind's
          preflight sets `display: none !important` on `[hidden]`, which beats
          the panel's own `flex`, and takes the hidden panels out of the
          accessibility tree and the tab order with it.
        */}
        {tabs.map((tab, i) => {
          const isActive = i === shownIndex;
          const ownDescription = tab.description?.trim() || '';
          return (
            <div
              key={tab.key ?? `item-${i}`}
              role="tabpanel"
              id={`${baseId}-panel-${i}`}
              aria-labelledby={`${baseId}-tab-${i}`}
              hidden={!isActive}
              className="flex flex-col gap-6 px-4 md:flex-row md:gap-12 md:px-16"
            >
              <div className="w-full md:w-1/2">
                <div
                  className="[&_a]:text-primary space-y-2 [&_h2]:mb-4 [&_h2]:text-lg [&_h2]:font-bold [&_h3]:mb-4 [&_h3]:text-lg [&_h3]:font-bold [&_h4]:mb-4 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5"
                  dangerouslySetInnerHTML={{
                    __html: sanitize(sub(tab.richText)),
                  }}
                />
                {tab.showPhone && phone && (
                  <a
                    href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                    className="text-primary mt-4 inline-flex items-center gap-2 font-bold"
                    aria-label={`Anrufen: ${phoneDisplay}`}
                  >
                    <Icon src="Phone" size={20} />
                    {phoneDisplay}
                  </a>
                )}
              </div>
              <div className="w-full md:w-1/2 [&_.px-4]:px-0 [&_.py-6]:py-0">
                {/*
                  A subject's own description is its own text, so it is written
                  into every panel. The widget-level zone and the fallback are
                  one piece of content shared by every subject that has no
                  description of its own — written into the shown panel only,
                  because repeating a dynamic zone per tab would repeat the ids
                  inside it (an anchor, a form field) and make the page's ids
                  ambiguous. It is in the HTML once, which is what indexing
                  needs.
                */}
                {ownDescription ? (
                  <div
                    className="text-foreground [&_a]:text-primary text-sm leading-relaxed lg:text-base [&_h2]:mb-4 [&_h3]:mb-4 [&_h4]:mb-4 [&_strong]:font-semibold"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(sub(ownDescription)),
                    }}
                  />
                ) : !isActive ? null : hasDescriptionZone ? (
                  <SlotRenderer
                    blocks={descriptionBlocks}
                    zoneName="description"
                  />
                ) : (
                  fallback && (
                    <div
                      className="text-foreground [&_a]:text-primary text-sm leading-relaxed lg:text-base [&_h2]:mb-4 [&_h3]:mb-4 [&_h4]:mb-4 [&_strong]:font-semibold"
                      dangerouslySetInnerHTML={{
                        __html: sanitize(sub(fallback)),
                      }}
                    />
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};
